import React, { Suspense } from 'react';
import type { OnboardingMapGeocode } from './SelaOnboardingMapPreview';

const SelaOnboardingMapPreview = React.lazy(() =>
  import('./SelaOnboardingMapPreview').then((module) => ({ default: module.SelaOnboardingMapPreview })),
);

type LocationMode = 'address' | 'parcel';

type OnboardingStepMapPanelProps = {
  locationMode: LocationMode;
  geocode: OnboardingMapGeocode | null;
};

function MapIntroOverlay({ geocode }: { geocode: OnboardingMapGeocode | null }) {
  return (
    <div className="pointer-events-none absolute left-8 top-8 z-10 max-w-md rounded-2xl bg-white/70 p-6 shadow-[0px_18px_42px_rgba(16,24,48,0.12)] backdrop-blur-md sm:left-10 sm:top-10">
      <p className="text-sm font-medium text-[#405973]">
        Map Preview {geocode ? `- ${geocode.label.split(',')[0]}` : '- select an address'}
      </p>
      <h2 className="mt-5 max-w-lg text-[30px] font-bold tracking-[-0.04em] text-[#10213a]">
        Turn a subject parcel into a predevelopment evaluation workspace in minutes.
      </h2>
      <p className="mt-4 max-w-xl text-[15px] leading-7 text-[#46627d]">
        Sela will carry this parcel into development paths, deal context, and priorities before creating the workspace.
      </p>
    </div>
  );
}

function MapLoadingFallback() {
  return (
    <div className="relative flex h-full min-h-[680px] items-center justify-center rounded-2xl bg-[#bfd1e0]">
      <MapIntroOverlay geocode={null} />
      <p className="text-sm text-[#46627d]">Loading map...</p>
    </div>
  );
}

function AddressEmptyState() {
  return (
    <div className="relative flex h-full min-h-[680px] items-center justify-center rounded-2xl bg-[#bfd1e0] px-8 text-center shadow-[0px_24px_64px_rgba(16,24,48,0.08)]">
      <MapIntroOverlay geocode={null} />
      <p className="mt-48 max-w-xs text-[15px] leading-7 text-[#46627d]">Select a verified address to preview the site on the map.</p>
    </div>
  );
}

function ParcelPlaceholderState() {
  return (
    <div className="relative flex h-full min-h-[680px] items-center justify-center rounded-2xl bg-[#bfd1e0] px-8 text-center shadow-[0px_24px_64px_rgba(16,24,48,0.08)]">
      <MapIntroOverlay geocode={null} />
      <p className="mt-48 max-w-xs text-[15px] leading-7 text-[#46627d]">Map preview is available when you search by address.</p>
    </div>
  );
}

export function OnboardingStepMapPanel({ locationMode, geocode }: OnboardingStepMapPanelProps) {
  if (locationMode === 'parcel') {
    return <ParcelPlaceholderState />;
  }

  if (!geocode) {
    return <AddressEmptyState />;
  }

  return (
    <Suspense fallback={<MapLoadingFallback />}>
      <div className="relative">
        <SelaOnboardingMapPreview geocode={geocode} />
        <MapIntroOverlay geocode={geocode} />
      </div>
    </Suspense>
  );
}
